import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SIGNING_STATUS_LABELS, type PortalAct, type PortalDispute, type PortalInvoice, type PortalTariff } from "@wms/domain";
import { Alert, App, Button, Drawer, Form, Input, Modal, Space, Table, Tabs, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { openPortalPrint, portalApi } from "../api/portal";
import { DocumentViewer } from "../components/DocumentViewer";
import { ApiError, errorMessage } from "../api/client";
import { formatDate, formatDateTime, formatQty } from "../api/format";
import { DISPUTE_COLORS, DISPUTE_REASON_MAX, disputeReasonProblem } from "./portalText";
import { SigningView, useProviders } from "./SigningView";
import { usePortalStore } from "./store";

interface Register {
  id: string;
  number: string;
  date: string;
  period_from: string;
  period_to: string;
  currency: string;
  status: string;
  total_gross: string;
}

const PAYMENT: Record<string, string> = { unpaid: "Не оплачен", partial: "Оплачен частично", paid: "Оплачен" };

/** C04 / C05: own invoices, registers and acts; «Подписать» / «Отказать» on the exact version; «Не согласен». */
export function PortalFinancePage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const can = usePortalStore((s) => s.can);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "invoices";
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [actId, setActId] = useState<string | null>(null);
  // ТЗ «Рабочие столы» 31.10.2026 (UXW-27): «Требует внимания» opens exactly this invoice or act on its tab — the same
  // card as its number in the list; the server answers for a document outside the cabinet
  useEffect(() => {
    const wanted = params.get("open");
    if (!wanted) return;
    if (tab === "invoices") setInvoiceId(wanted);
    else if (tab === "acts") setActId(wanted);
    params.delete("open");
    setParams(params, { replace: true });
  }, [params, setParams, tab]);
  // KZ-DOC TMP21 / UI05: own invoices, registers and acts open in the same PDF viewer; «Скачать PDF» and «Печать» take
  // the stored file. The HTML print form stays next to it as it was.
  const [pdf, setPdf] = useState<{ kind: string; id: string; title: string; legacy?: string } | null>(null);
  const invoices = useQuery({ queryKey: ["portal", "invoices"], queryFn: () => portalApi.get<PortalInvoice[]>("/finance/invoices") });
  const registers = useQuery({ queryKey: ["portal", "registers"], queryFn: () => portalApi.get<Register[]>("/finance/registers") });
  const acts = useQuery({ queryKey: ["portal", "acts"], queryFn: () => portalApi.get<PortalAct[]>("/finance/acts") });
  // UX3010-09 / ПР20: the client sees the price its services are calculated at; only the warehouse changes it
  const tariffs = useQuery({ queryKey: ["portal", "tariffs"], queryFn: () => portalApi.get<PortalTariff[]>("/tariffs"), enabled: tab === "tariffs" });
  const invoice = useQuery({ queryKey: ["portal", "invoice", invoiceId], queryFn: () => portalApi.get<PortalInvoice>(`/finance/invoices/${invoiceId}`), enabled: !!invoiceId });
  const act = useQuery({ queryKey: ["portal", "act", actId], queryFn: () => portalApi.get<PortalAct>(`/finance/acts/${actId}`), enabled: !!actId });
  const providers = useProviders(() => portalApi.get("/providers"), !!actId);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["portal", "acts"] });
    queryClient.invalidateQueries({ queryKey: ["portal", "act", actId] });
    queryClient.invalidateQueries({ queryKey: ["portal", "dashboard"] });
  };
  const sign = useMutation({
    mutationFn: (body: Record<string, unknown>) => portalApi.post<{ status: string; package_status: string; verification: string }>(`/finance/acts/${actId}/sign`, body),
    onSuccess: (r) => {
      message.success(r.status === "signed" ? `Подписано. Состояние пакета: ${SIGNING_STATUS_LABELS[r.package_status] ?? r.package_status}` : "Подпись принята, результат проверки пока неизвестен");
      refresh();
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  const reject = useMutation({
    mutationFn: ({ reason, expected_hash }: { reason: string; expected_hash: string }) =>
      portalApi.post(`/finance/acts/${actId}/reject`, { reason, expected_hash }),
    onSuccess: () => {
      message.success("Отказ зафиксирован, склад увидит основание");
      refresh();
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  // CW31-B10: the client's own disputes with the answer of the warehouse — in the card of the document and in a list
  const disputes = useQuery({ queryKey: ["portal", "disputes"], queryFn: () => portalApi.get<PortalDispute[]>("/finance/disputes") });
  const disputesOf = (documentId: string) => (disputes.data ?? []).filter((d) => d.document_id === documentId);
  // a card opened again shows the answer as it is now, not as it was when the page was loaded
  useEffect(() => {
    if (invoiceId || actId) void disputes.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, actId]);
  // CW31-B14: «Не согласен» needs the client's own explanation — checked here (the dialog stays open with the text)
  // and on the server; nothing is put in place of an empty field
  const [disputeFor, setDisputeFor] = useState<{ type: string; id: string; number: string } | null>(null);
  const [disputeForm] = Form.useForm<{ reason: string }>();
  const createDispute = useMutation({
    mutationFn: (body: { document_type: string; document_id: string; reason: string }) =>
      portalApi.post<{ id: string; status: string; document_number: string }>("/finance/disputes", body),
    onSuccess: () => {
      message.success("Обращение на сверку создано; складской факт и сумма долга не меняются автоматически. Ответ склада появится в «Мои обращения»");
      queryClient.invalidateQueries({ queryKey: ["portal", "disputes"] });
      setDisputeFor(null);
      disputeForm.resetFields();
    },
    onError: (e) => {
      const errors = e instanceof ApiError && Array.isArray(e.problem.errors) ? (e.problem.errors as { field?: string; message?: string }[]) : [];
      const own = errors.find((item) => item.field === "reason");
      if (own) disputeForm.setFields([{ name: "reason", errors: [own.message ?? "Проверьте пояснение к обращению"] }]);
      else message.error(errorMessage(e));
    },
  });
  const dispute = (document_type: string, document_id: string, number: string) => {
    disputeForm.resetFields();
    setDisputeFor({ type: document_type, id: document_id, number });
  };
  const openDocument = (d: PortalDispute) => {
    if (d.document_type === "invoice") setInvoiceId(d.document_id);
    else if (d.document_type === "act") setActId(d.document_id);
    else {
      params.set("tab", "registers");
      setParams(params, { replace: true });
    }
  };
  const inv = invoice.data;
  const a = act.data;
  return (
    <div>
      <div className="wms-page-header">
        <div>
          <h1>Документы и счета</h1>
          <div className="wms-subtitle">Счета, реестры услуг и акты выполненных работ; оплата и подпись — отдельные состояния</div>
        </div>
      </div>
      <Tabs
        activeKey={tab}
        onChange={(key) => {
          params.set("tab", key);
          setParams(params, { replace: true });
        }}
        items={[
          {
            key: "invoices",
            label: "Счета",
            children: (
              <>
                {invoices.isError && <Alert type="error" showIcon message={errorMessage(invoices.error)} />}
                <Table<PortalInvoice>
                  rowKey="id"
                  size="middle"
                  loading={invoices.isLoading}
                  dataSource={invoices.data ?? []}
                  pagination={{ pageSize: 25 }}
                  scroll={{ x: true }}
                  locale={{ emptyText: "Счетов пока нет" }}
                  columns={[
                    { title: "Номер", dataIndex: "number", render: (v: string, r: PortalInvoice) => <a onClick={() => setInvoiceId(r.id)}>{v}</a> },
                    { title: "Дата", dataIndex: "date", render: (v: string) => formatDate(v) },
                    { title: "Оплатить до", dataIndex: "due_date", render: (v: string | null) => (v ? formatDate(v) : "") },
                    { title: "Сумма", dataIndex: "total_gross", align: "right" as const, render: (v: string, r: PortalInvoice) => `${v} ${r.currency}` },
                    { title: "Оплата", dataIndex: "payment_status", render: (v: string) => <Tag color={v === "paid" ? "green" : v === "partial" ? "gold" : "default"}>{PAYMENT[v] ?? v}</Tag> },
                    { title: "Передан", dataIndex: "delivered_at", render: (v: string | null) => (v ? formatDateTime(v) : "") },
                  ]}
                />
              </>
            ),
          },
          {
            key: "registers",
            label: "Реестры услуг",
            children: (
              <Table<Register>
                rowKey="id"
                size="middle"
                loading={registers.isLoading}
                dataSource={registers.data ?? []}
                pagination={{ pageSize: 25 }}
                scroll={{ x: true }}
                locale={{ emptyText: "Реестров пока нет" }}
                // CW31-B10: the disputes of a register open under its row — a register has no card of its own
                expandable={{
                  rowExpandable: (r: Register) => disputesOf(r.id).length > 0,
                  expandedRowRender: (r: Register) => <DisputesTable items={disputesOf(r.id)} compact />,
                }}
                columns={[
                  { title: "Номер", dataIndex: "number" },
                  { title: "Период", key: "period", render: (_: unknown, r: Register) => `${formatDate(r.period_from)} — ${formatDate(r.period_to)}` },
                  { title: "Сумма", dataIndex: "total_gross", align: "right" as const, render: (v: string, r: Register) => `${v} ${r.currency}` },
                  {
                    title: "",
                    key: "x",
                    render: (_: unknown, r: Register) => (
                      <Space size={4} wrap>
                        <Button size="small" type="primary" onClick={() => setPdf({ kind: "service_register", id: r.id, title: `Реестр ${r.number}` })} data-testid={`portal-register-pdf-${r.number}`}>
                          Открыть PDF
                        </Button>
                        <Button size="small" onClick={() => dispute("register", r.id, r.number)}>Не согласен</Button>
                        {disputesOf(r.id).length > 0 && <Tag>Обращений: {disputesOf(r.id).length}</Tag>}
                      </Space>
                    ),
                  },
                ]}
              />
            ),
          },
          {
            key: "tariffs",
            label: "Тарифы",
            children: (
              <>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Тарифы устанавливает склад"
                  description="Стоимость услуги считается по ставке, действовавшей на дату операции. Изменить тариф из кабинета нельзя — по вопросам обращайтесь к вашему менеджеру."
                />
                {tariffs.isError && <Alert type="error" showIcon message={errorMessage(tariffs.error)} />}
                <Table<PortalTariff>
                  rowKey="id"
                  size="middle"
                  loading={tariffs.isLoading}
                  dataSource={tariffs.data ?? []}
                  pagination={{ pageSize: 50, hideOnSinglePage: true }}
                  scroll={{ x: true }}
                  data-testid="portal-tariffs"
                  locale={{ emptyText: "Тарифов пока нет" }}
                  columns={[
                    { title: "Услуга", dataIndex: "service", render: (v: string, r: PortalTariff) => <>{v} {r.personal ? <Tag color="blue">ваш тариф</Tag> : <Tag>общий</Tag>}</> },
                    { title: "Тип обработки", dataIndex: "handling_label" },
                    { title: "Единица", dataIndex: "unit_label" },
                    { title: "Ставка", dataIndex: "rate", align: "right" as const, render: (v: string, r: PortalTariff) => `${v} ${r.currency}` },
                    { title: "Налог", dataIndex: "tax_label", render: (v: string, r: PortalTariff) => (r.tax_rate ? `${v}, ${r.tax_rate}%` : v) },
                    {
                      title: "Действует",
                      key: "period",
                      render: (_: unknown, r: PortalTariff) => (
                        <>
                          с {formatDate(r.effective_from)}
                          {r.effective_to ? ` по ${formatDate(r.effective_to)}` : ""}
                          {!r.current && <Tag color="gold" style={{ marginLeft: 6 }}>с будущей даты</Tag>}
                        </>
                      ),
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: "acts",
            label: "АВР и подписание",
            children: (
              <Table<PortalAct>
                rowKey="id"
                size="middle"
                loading={acts.isLoading}
                dataSource={acts.data ?? []}
                pagination={{ pageSize: 25 }}
                scroll={{ x: true }}
                locale={{ emptyText: "Актов пока нет" }}
                columns={[
                  { title: "Номер", dataIndex: "number", render: (v: string, r: PortalAct) => <a onClick={() => setActId(r.id)} data-testid={`portal-act-${v}`}>{v}</a> },
                  { title: "Дата", dataIndex: "date", render: (v: string) => formatDate(v) },
                  { title: "Сумма", dataIndex: "total_gross", align: "right" as const, render: (v: string, r: PortalAct) => `${v} ${r.currency}` },
                  { title: "Состояние", dataIndex: "status", render: (v: string, r: PortalAct) => <Tag color={r.package?.can_sign ? "processing" : v === "signed" ? "green" : "default"}>{SIGNING_STATUS_LABELS[r.package?.status ?? v] ?? v}</Tag> },
                  { title: "", key: "x", render: (_: unknown, r: PortalAct) => (r.package?.can_sign && can("portal.documents.sign") ? <Button size="small" type="primary" onClick={() => setActId(r.id)}>Подписать</Button> : null) },
                ]}
              />
            ),
          },
          {
            // CW31-B10: every own dispute with its status and the answer of the warehouse
            key: "disputes",
            label: "Мои обращения",
            children: (
              <>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Обращения на сверку и ответы склада"
                  description="Обращение создаётся кнопкой «Не согласен» в счёте, реестре или АВР. Сумма долга, подписи и складской факт от обращения сами не меняются: склад проверяет и отвечает здесь."
                />
                {disputes.isError && <Alert type="error" showIcon message={errorMessage(disputes.error)} style={{ marginBottom: 12 }} />}
                <DisputesTable items={disputes.data ?? []} loading={disputes.isLoading} onOpen={openDocument} />
              </>
            ),
          },
        ]}
      />
      {pdf && <DocumentViewer open onClose={() => setPdf(null)} channel="portal" kind={pdf.kind} documentId={pdf.id} title={pdf.title} legacyPath={pdf.legacy ?? null} />}
      <Drawer open={!!invoiceId} onClose={() => setInvoiceId(null)} width={720} title={inv ? `Счёт ${inv.number}` : "Счёт"}>
        {invoice.isError && <Alert type="error" showIcon message={errorMessage(invoice.error)} />}
        {inv && (
          <>
            <Space wrap style={{ marginBottom: 12 }}>
              <Button type="primary" onClick={() => setPdf({ kind: "invoice", id: inv.id, title: `Счёт ${inv.number}`, legacy: `/finance/invoices/${inv.id}/print` })} data-testid="portal-invoice-pdf">
                Открыть PDF
              </Button>
              <Button onClick={() => openPortalPrint(`/finance/invoices/${inv.id}/print`).catch((e) => message.error(errorMessage(e)))}>Скачать / распечатать</Button>
              <Button onClick={() => dispute("invoice", inv.id, inv.number)}>Не согласен</Button>
              <Tag>{PAYMENT[inv.payment_status] ?? inv.payment_status}</Tag>
            </Space>
            <Table
              rowKey="line_no"
              size="small"
              pagination={false}
              dataSource={inv.lines ?? []}
              columns={[
                { title: "№", dataIndex: "line_no", width: 50 },
                { title: "Услуга", dataIndex: "description" },
                { title: "Кол-во", dataIndex: "quantity", align: "right" as const, render: (v: unknown) => formatQty(v) },
                // CW31-B09: «шт», not `piece`
                { title: "Ед.", dataIndex: "unit_code", render: (v: string, r: NonNullable<PortalInvoice["lines"]>[number]) => r.unit_label ?? v },
                { title: "Ставка", dataIndex: "rate", align: "right" as const },
                { title: "С налогом", dataIndex: "gross", align: "right" as const },
              ]}
            />
            <p style={{ textAlign: "right", marginTop: 8 }}>
              Итого: <b>{inv.total_gross} {inv.currency}</b>, оплачено {inv.paid_amount}
            </p>
            <DocumentDisputes items={disputesOf(inv.id)} failed={disputes.isError} />
          </>
        )}
      </Drawer>
      <Drawer open={!!actId} onClose={() => setActId(null)} width={820} title={a ? `АВР ${a.number}` : "АВР"}>
        {act.isError && <Alert type="error" showIcon message={errorMessage(act.error)} />}
        {a && (
          <>
            <Space wrap style={{ marginBottom: 12 }}>
              <Button type="primary" onClick={() => setPdf({ kind: "act", id: a.id, title: `АВР ${a.number}`, legacy: `/finance/acts/${a.id}/print` })} data-testid="portal-act-pdf">
                Открыть PDF
              </Button>
              <Button onClick={() => openPortalPrint(`/finance/acts/${a.id}/print`).catch((e) => message.error(errorMessage(e)))}>Скачать / распечатать</Button>
              <Button onClick={() => dispute("act", a.id, a.number)}>Не согласен</Button>
            </Space>
            {a.package ? (
              <SigningView
                view={a.package}
                providers={providers.data}
                canSign={can("portal.documents.sign")}
                canReject={can("portal.documents.reject")}
                busy={sign.isPending || reject.isPending}
                onSign={(body) => sign.mutate(body)}
                onReject={(reason, expected_hash) => reject.mutate({ reason, expected_hash })}
              />
            ) : (
              <Alert type="info" showIcon message="Версия для подписания ещё не зафиксирована складом" />
            )}
            <DocumentDisputes items={disputesOf(a.id)} failed={disputes.isError} />
          </>
        )}
      </Drawer>
      <Modal
        open={!!disputeFor}
        title={disputeFor ? `Не согласен с документом ${disputeFor.number}` : ""}
        okText="Отправить на сверку"
        cancelText="Вернуться"
        confirmLoading={createDispute.isPending}
        onOk={() => disputeForm.submit()}
        onCancel={() => setDisputeFor(null)}
        forceRender
        // CW31-B14: opened from the card of a document (a Drawer of the same level) — it must lie above that card
        zIndex={1100}
      >
        <Form
          form={disputeForm}
          layout="vertical"
          onFinish={(values) => disputeFor && createDispute.mutate({ document_type: disputeFor.type, document_id: disputeFor.id, reason: values.reason.trim() })}
        >
          <Form.Item
            name="reason"
            label="Поясните, с чем вы не согласны"
            required
            extra="Например: «Строка 2: принято 150 шт, а не 160» или «Ставка за хранение не та, что в договоре». Склад ответит в разделе «Мои обращения»."
            rules={[{ validator: (_, value: string | undefined) => { const problem = disputeReasonProblem(value); return problem ? Promise.reject(new Error(problem)) : Promise.resolve(); } }]}
          >
            <Input.TextArea rows={4} count={{ show: true, max: DISPUTE_REASON_MAX }} data-testid="portal-dispute-reason" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

/** CW31-B10: the disputes of one document in its card — the status, the reason, the answer of the warehouse. */
function DocumentDisputes({ items, failed }: { items: PortalDispute[]; failed?: boolean }) {
  return (
    <div style={{ marginTop: 16 }} data-testid="portal-document-disputes">
      <Typography.Title level={5}>Мои обращения</Typography.Title>
      {failed ? (
        <Alert type="warning" showIcon message="Не удалось загрузить обращения: обновите страницу" />
      ) : items.length === 0 ? (
        <Typography.Text type="secondary">По этому документу обращений нет. Если не согласны — нажмите «Не согласен».</Typography.Text>
      ) : (
        <DisputesTable items={items} compact />
      )}
    </div>
  );
}

/** CW31-B10: own disputes in words — never the codes of statuses or documents. */
function DisputesTable({ items, loading, compact, onOpen }: { items: PortalDispute[]; loading?: boolean; compact?: boolean; onOpen?: (d: PortalDispute) => void }) {
  return (
    <Table<PortalDispute>
      rowKey="id"
      size="small"
      loading={loading}
      dataSource={items}
      pagination={compact ? false : { pageSize: 25, hideOnSinglePage: true }}
      scroll={{ x: true }}
      locale={{ emptyText: "Обращений пока нет" }}
      data-testid={compact ? undefined : "portal-disputes"}
      columns={[
        ...(compact
          ? []
          : [{ title: "Документ", dataIndex: "document_label", render: (v: string, d: PortalDispute) => (onOpen ? <a onClick={() => onOpen(d)}>{v}</a> : v) }]),
        { title: "Создано", dataIndex: "created_at", render: (v: string) => formatDateTime(v) },
        { title: "Ваше пояснение", dataIndex: "reason" },
        { title: "Состояние", dataIndex: "status_label", render: (v: string, d: PortalDispute) => <Tag color={DISPUTE_COLORS[d.status] ?? "default"}>{v}</Tag> },
        { title: "Ответ склада", dataIndex: "resolution", render: (v: string | null) => v || <Typography.Text type="secondary">Ответа пока нет</Typography.Text> },
        {
          title: "Решено",
          dataIndex: "resolved_at",
          render: (v: string | null, d: PortalDispute) => (v ? `${formatDateTime(v)}${d.resolved_by_name ? `, ${d.resolved_by_name}` : ""}` : d.resolved_by_name ? `рассматривает ${d.resolved_by_name}` : ""),
        },
      ]}
    />
  );
}
